import { useState } from 'react';
import { toast } from 'sonner';

import { CopyIcon, Cross2Icon, DownloadIcon } from '@radix-ui/react-icons';

import { logger } from '@/shared/lib/logger';
import { useImageViewerStore } from '@/shared/stores/image-viewer-store';
import { useSelectedMediaStore } from '@/shared/stores/selected-media-store';
import { useSelectedPersonaStore } from '@/shared/stores/selected-persona-store';
import { ModalPortal } from '@/shared/ui';

export const ImageViewerModal = () => {
  const { isOpen, imageUrl, activeModel, isNSFW, prompt, closeImage } = useImageViewerStore();
  const setSelectedPersona = useSelectedPersonaStore((state) => state.setSelectedPersona);
  const setSelectedImage = useSelectedMediaStore((state) => state.setSelectedImage);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCopyPrompt = async () => {
    if (!prompt) return;

    try {
      await navigator.clipboard.writeText(prompt);
      toast.success('Prompt copied to clipboard!');
    } catch (error) {
      logger.ui.error('Failed to copy prompt', { error });
      toast.error('Failed to copy prompt');
    }
  };

  const handleDownload = async () => {
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.ui.error('Failed to download image', { error, imageUrl });
    }
  };

  const handleMakeVideo = () => {
    if (!activeModel || !imageUrl) return;

    // Set the selected persona in the global store
    setSelectedPersona({
      id: activeModel.id,
      name: activeModel.name,
      isMine: true, // Default to true since this is coming from user's models
    });

    // Save the selected image URL for Video Studio
    setSelectedImage(imageUrl);

    // Close the modal
    closeImage();

    // Navigate to Video Studio (only regular, not NSFW)
    window.location.href = '/video-studio';
  };

  if (!imageUrl) return null;

  return (
    <ModalPortal isOpen={isOpen} onClose={closeImage}>
      <div className="relative max-h-[90vh] flex">
        {/* Image Section */}
        <div className="relative">
          <img
            src={imageUrl}
            alt="Full Size"
            className="max-h-[90vh] w-auto object-contain"
            onLoad={() => setImageLoaded(true)}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Close Button - positioned over the image */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeImage();
            }}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <Cross2Icon className="w-5 h-5" />
          </button>

          {/* Download Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="absolute top-4 right-16 z-20 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors cursor-pointer"
            aria-label="Download"
          >
            <DownloadIcon className="w-5 h-5" />
          </button>

          {/* Make Video Button - Only for regular Image Studio */}
          {activeModel && !isNSFW && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMakeVideo();
              }}
              className="absolute top-4 right-28 z-20 px-3 py-2 bg-primary/80 backdrop-blur-sm rounded-lg text-white text-sm font-medium hover:bg-primary/80 transition-colors cursor-pointer"
              aria-label="Make Video"
            >
              Make Video
            </button>
          )}

          {/* Loading state */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          )}
        </div>

        {/* Right Section with Prompt - 150px width */}
        <div className="w-[150px] p-4 flex flex-col justify-center">
          {/* Prompt Section */}
          {prompt && (
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs text-white leading-relaxed break-words flex-1">
                  {prompt}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt();
                  }}
                  className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-white hover:bg-black/30 transition-colors cursor-pointer flex-shrink-0"
                  aria-label="Copy Prompt"
                  title="Copy prompt"
                >
                  <CopyIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};
